import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router } from "react-router-dom";
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { theme } from './styles/theme';
import './index.css';
import App from './App';

// MSAL imports
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { msalConfig, loginRequest } from "./authConfig";
import { setMsalInstance } from "./utils/msalSingleton";
import { luisterNaarUitloggen, meldUitgelogd, onthoudAccount, heeftStilControle } from "./utils/stilInloggen";
import axios from "axios";

// Suppress ResizeObserver loop error (harmless browser bug with MUI)
// This error occurs when ResizeObserver cannot deliver all observations in a single animation frame
// It's benign and doesn't affect functionality
if (typeof window !== 'undefined') {
  // Suppress via window.onerror
  const originalOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    if (message && typeof message === 'string' && message.includes('ResizeObserver')) {
      return true;
    }
    if (originalOnError) {
      return originalOnError(message, source, lineno, colno, error);
    }
    return false;
  };

  // Suppress via addEventListener
  window.addEventListener('error', (e) => {
    if (e.message && e.message.includes('ResizeObserver')) {
      e.stopImmediatePropagation();
      e.stopPropagation();
      e.preventDefault();
    }
  }, true);

  // Suppress unhandled rejection (for promise-based errors)
  window.addEventListener('unhandledrejection', (e) => {
    if (e.reason && e.reason.message && e.reason.message.includes('ResizeObserver')) {
      e.preventDefault();
    }
  });

  // Override ResizeObserver to debounce and prevent the loop error
  const OriginalResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class ResizeObserver extends OriginalResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        // Use requestAnimationFrame to batch updates
        window.requestAnimationFrame(() => {
          try {
            callback(entries, observer);
          } catch (e) {
            // Silently ignore ResizeObserver errors
          }
        });
      });
    }
  };
}

export const msalInstance = new PublicClientApplication(msalConfig);
setMsalInstance(msalInstance);

// Global axios interceptor — adds Bearer token to all /Api/ requests
axios.interceptors.request.use(async (config) => {
  if (config.url && config.url.includes('/Api/')) {
    try {
      const account = msalInstance.getActiveAccount();
      if (account) {
        const response = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
        config.headers.Authorization = `Bearer ${response.idToken}`;
      }
    } catch { /* silent fail — backend returns 401 */ }
  }
  return config;
});

msalInstance.initialize().then(() => {
    
  // Default to using the first account if no account is active on page load
  // Alleen automatisch kiezen als er precies één account is; bij meerdere accounts
  // (gedeelde edit-machine) beslist de AccountPicker — anders werk je ongemerkt onder
  // de identiteit van een collega (P0 5 sep, FE-04; zelfde regel als authHelpers).
  const alleAccounts = msalInstance.getAllAccounts();
  if (!msalInstance.getActiveAccount() && alleAccounts.length === 1) {
    msalInstance.setActiveAccount(alleAccounts[0]);
  }

  // Optional - This will update account state if a user signs in from another tab or window
  msalInstance.enableAccountStorageEvents();

  // Uitloggen in één tabblad = uitloggen in alle tabbladen. De MSAL-cache staat per
  // tabblad in sessionStorage, dus de andere tabbladen horen het via een BroadcastChannel.
  luisterNaarUitloggen(msalInstance);
  // Wie hier ingelogd is, mag een nieuw tabblad stil hergebruiken (alleen de gebruikersnaam).
  if (msalInstance.getActiveAccount() && !heeftStilControle()) {
    onthoudAccount(msalInstance.getActiveAccount());
  }

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGOUT_START) {
      meldUitgelogd();
    }
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
      const account = event.payload.account;
      msalInstance.setActiveAccount(account);
      // Na een stille prompt=none-redirect eerst de identiteitscontrole (StilInloggen).
      if (!heeftStilControle()) onthoudAccount(account);
    }
  });

  const container = document.getElementById("root");
  const root = ReactDOM.createRoot(container);

  root.render(
    <Router>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        <App pca={msalInstance} />
      </MuiThemeProvider>
    </Router>
  );
});